import { HttpParams } from '@angular/common/http';
import { AfterContentInit, Component, ElementRef, forwardRef, Inject, Input, OnInit } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { USER_HISTORICAL_ALERTS_V2 } from '../../common/endpoints';
import { Alert, PagedList } from '../../model';
import { AppService } from '../../service/app.service';
import { AuthenticationService } from '../../service/authentication.service';
import { HttpService } from '../../service/http.service';
import { CustomTableColumn, CustomTableService } from '../../shared/custom-table';
import { ListWidgetV2Component } from '../../shared/list-widget-v2/list-widget-v2.components';
import { ErrorUtility } from '../../utility/error-utility';
import { WidgetPrintResizeCoordinatorService } from '../../widget/shared/widget-print-resize-coordinator.service';

@Component({
    selector: 'event-details-occurrences',
    template: require('./event-details-occurrences.component.html'),
    styles: [`
        :host {
            display: block;
        }

        custom-table ::ng-deep tr.mat-row:hover {
            cursor: default;
            background-color: inherit;
        }
    `]
})
export class EventDetailsOccurrencesComponent extends ListWidgetV2Component<Alert> implements OnInit, AfterContentInit {

    @Input() alert: Alert;

    constructor(
        @Inject(forwardRef(() => AuthenticationService)) authenticationService: AuthenticationService,
        @Inject(forwardRef(() => AppService)) appService: AppService,
        @Inject(forwardRef(() => HttpService)) private httpService: HttpService,
        @Inject(forwardRef(() => ElementRef)) elementRef: ElementRef,
        @Inject(forwardRef(() => WidgetPrintResizeCoordinatorService)) coordinator: WidgetPrintResizeCoordinatorService
    ) {
        super(appService, authenticationService, null, null, null, null, null, elementRef, coordinator);
    }

    ngOnInit(): void {
        this.pageSize = 10;
        this.sort = ['startTimestamp', 'desc'];
        this.fillerRowCount = 10;
        this.fillerRowHeight = 30;
        this.fillerRowHeightWithSpace = 36;
        this.fillerRowOffset = 30;
    }

    ngAfterContentInit(): void {
        this.displayedColumns = this.getDisplayedColumns();
        this.getOccurrences();
    }

    private getDisplayedColumns(): CustomTableColumn[] {
        return [
            CustomTableService.newDatetimeColumn('date', 'dateProperty', 'startTimestamp', 'L'),
            CustomTableService.newSimpleColumn('title', 'titleProperty', 'title'),
            CustomTableService.newPipedColumn('severity', 'severityProperty', 'severity', 'alertSeverity'),
            CustomTableService.newTimerColumn('duration', 'alertDurationProperty', 'startTimestamp', 'endTimestamp')
        ];
    }

    refreshList(data?: { pageIndex: number, pageSize: number, advancedSearchBody: any, sort: string[] }): void {
        if (data) {
            this.pageIndex = data.pageIndex;
            this.pageSize = data.pageSize;
            this.sort = data.sort;
        }
        this.getOccurrences();
    }

    protected goToDetail(element: Alert): void {
        /* occurrence rows are informational, as in the Action detail history table */
    }

    protected subscribeToExportServices(): void {
        /* no export from the event details section */
    }

    private getOccurrences(): void {
        this.loaded = false;
        this.error = null;
        // 1. Without a Thing and an event definition there is nothing to query.
        const thingId = this.alert?.thing?.id || (this.alert as any)?.thingId;
        if (!thingId || !this.alert?.alertDefinitionId) {
            this.elements = [];
            this.loaded = true;
            return;
        }
        // 2. Ask for one page of the historical events of this definition on this Thing.
        let params = new HttpParams()
            .set('page', this.pageIndex + '')
            .set('size', this.pageSize + '')
            .set('thingId', thingId)
            .set('alertDefinitionId', this.alert.alertDefinitionId);
        if (this.sort?.length) {
            params = params.set('sort', this.sort.join(','));
        }
        firstValueFrom(this.httpService.get<PagedList<Alert>>(USER_HISTORICAL_ALERTS_V2, params))
            .then(pagedList => {
                // 3. Hide the event being displayed and fall back to the name when a title is missing.
                const occurrences = (pagedList.content || []).filter(occurrence => occurrence.id !== this.alert.id);
                occurrences.forEach(occurrence => occurrence['title'] = occurrence.title || occurrence.name);
                // 4. Replace only the rows; keep the server's page numbers so the paginator stays correct.
                this.updateElementList(Object.assign({}, pagedList, { content: occurrences }), true);
            })
            // 5. A failed request shows the error in the section.
            .catch(err => {
                this.loaded = true;
                this.error = ErrorUtility.getMessage(err);
            });
    }
}
